'use client';

/* eslint-disable no-inline-styles/no-inline-styles -- this component's job is
   to drive fade/translate animations via inline style; the hidden start state
   must be inline so it is not lost on a slow CSS load (FOUC). */
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

// Shared props for the two inner variants. `animateOnMount` lives only on
// the public type below because the parent uses it to choose between them.
type FadeProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  // `contain: paint` clips anything that overflows the box. That is fine for
  // most content but shaves the top of large display-font glyphs whose
  // ascenders poke above a tight line box (e.g. section headings), and cuts
  // off the 4px focus outline of inputs sitting at the box edge (the search
  // and filter fields on listing pages). Callers that wrap such content opt
  // out; every other call keeps the default.
  containPaint?: boolean;
};

type ScrollFadeSectionProps = FadeProps & {
  animateOnMount?: boolean;
};

// Above-the-fold/hero content. Uses a CSS animation so the browser can
// start the staggered fade-in as soon as the HTML is parsed, without
// waiting for React to hydrate. Framer-motion's initial="hidden" left
// the hero blank during hydration which felt like a second reload.
//
// The hidden start state (opacity/translateY) is set INLINE, not left to the
// `scroll-fade-mount` rule in global.css alone. If it lived only in the external
// stylesheet, a slow CSS load lets the browser paint the hero fully visible
// first, then snap it hidden and re-fade once the stylesheet applies, which
// reads as a second page load (FOUC). Inline it is applied with the HTML. The
// keyframe still drives the fade and its forwards fill overrides these to the
// visible end state.
const MountFade = ({ children, className, delay = 0.1, duration = 0.3 }: FadeProps) => (
  <div
    className={`scroll-fade-mount ${className || ''}`.trim()}
    style={{
      opacity: 0,
      transform: 'translateY(16px)',
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  >
    {children}
  </div>
);

// Below-the-fold content. Behaves identically to the previous
// framer-motion `useInView` + `motion.div` implementation (2% threshold,
// -50px bottom rootMargin, fires once and stays visible) but uses a
// native IntersectionObserver + CSS transition. Each landing page has
// 20+ ScrollFadeSection instances so the savings compound:
//   - no per-instance framer-motion render/state machine
//   - no framer-motion animation runtime on the main thread
//   - CSS transitions run on the compositor (GPU) instead of JS
//   - SSR markup is unchanged so there is no hydration mismatch
const OBSERVER_OPTIONS: IntersectionObserverInit = {
  threshold: 0.02,
  rootMargin: '0px 0px -50px 0px',
};

const FadeOnScroll = ({ children, className, delay = 0.1, duration = 0.3, containPaint = true }: FadeProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // Browsers without IntersectionObserver fall through to immediately
    // visible so content is never stuck hidden.
    if (!('IntersectionObserver' in window)) {
      setVisible(true);

      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, OBSERVER_OPTIONS);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const style: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(16px)',
    transition: `opacity ${duration}s ease-in-out ${delay}s, transform ${duration}s ease-in-out ${delay}s`,
    contain: containPaint ? 'layout style paint' : 'layout style',
    willChange: visible ? undefined : 'opacity, transform',
  };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

const ScrollFadeSection = ({ animateOnMount, ...rest }: ScrollFadeSectionProps) => {
  if (animateOnMount) {
    return <MountFade {...rest} />;
  }

  return <FadeOnScroll {...rest} />;
};

export default ScrollFadeSection;
